import type { PopulatedOrgMember } from "@api/types";
import { Check, UserX } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { getUserName, TaskUserAvatar } from "./shared";

interface AssigneePickerProps {
  /** Selected assignee user id, or null for unassigned */
  value: string | null;
  members?: PopulatedOrgMember[];
  onChange: (userId: string | null) => void;
  align?: "start" | "center" | "end";
  children: ReactNode;
}

export function AssigneePicker({
  value,
  members,
  onChange,
  align = "start",
  children,
}: AssigneePickerProps) {
  const [open, setOpen] = useState(false);

  function select(userId: string | null) {
    onChange(userId);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align={align}>
        <Command>
          <CommandInput className="text-sm" placeholder="Assign to..." />
          <CommandList>
            <CommandEmpty className="p-4 text-sm">No members found.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="unassigned" onSelect={() => select(null)} className="text-sm">
                <Check
                  className={cn("mr-2 size-4", value === null ? "opacity-100" : "opacity-0")}
                />
                <UserX className="mr-2 size-4 text-muted-foreground" />
                Unassigned
              </CommandItem>
              {(members ?? []).map((member) => (
                <CommandItem
                  key={member.user.id}
                  value={getUserName(member.user)}
                  onSelect={() => select(member.user.id)}
                  className="text-sm"
                >
                  <Check
                    className={cn(
                      "mr-2 size-4",
                      value === member.user.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <TaskUserAvatar user={member.user} className="mr-2" />
                  {getUserName(member.user)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
